import { z } from 'zod';
import { recordEntries } from 'libs/end-to-end/src/arrays';
import { safeParseInt } from 'libs/end-to-end/src/math';
import { Result } from 'libs/end-to-end/src/result';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { BufferToStringError } from 'libs/end-to-end/src/types/errors/buffer-to-string-error';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { S3StorageError } from 'libs/end-to-end/src/types/errors/s3-storage-error';
import { RaceTimeoutError } from 'libs/end-to-end/src/types/errors/race-timeout-error';

export const GenericSuccess = z.object({
  success: z.literal(true),
});

export type GenericSuccess = z.infer<typeof GenericSuccess>;

export const EmptyObject = z
  .record(z.string(), z.never())
  .describe('EmptyObject');
export type EmptyObject = z.infer<typeof EmptyObject>;

export const UnknownObject = z.unknown();
export type UnknownObject = z.infer<typeof UnknownObject>;

export const Configurations = z.record(z.string(), z.string());
export type Configurations = z.infer<typeof Configurations>;

export const MappedConfigurations = z.record(z.string(), Configurations);
export type MappedConfigurations = z.infer<typeof MappedConfigurations>;

// This forces us to think about what we're actually passing to the script.
export const OptionalStringArray = z.array(z.string()).nullable();
export type OptionalStringArray = z.infer<typeof OptionalStringArray>;

// Same as above, but only allows one item.
export const OptionalSingleSelectStringArray = z
  .string()
  .array()
  .max(1)
  .nullable();
export type OptionalSingleSelectStringArray = z.infer<
  typeof OptionalSingleSelectStringArray
>;

export const rotate = <T>(arr: T[], first: T): T[] => {
  const index = arr.indexOf(first);
  return [...arr.slice(index), ...arr.slice(0, index)];
};

export const enumValueFromId = <TEnum extends string>(
  id: number,
  idsMap: Record<TEnum, number>,
): TEnum | null => {
  const idAsInt = safeParseInt(id.toString(), 10);

  if (idAsInt === null) {
    return null;
  }

  for (const [key, value] of recordEntries(idsMap)) {
    if (value === idAsInt) {
      return key;
    }
  }

  return null;
};

export const waitForMs = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const guardExhaustive = (exhaustiveCheck: never) => {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return new Error(`Unhandled case: ${exhaustiveCheck}`);
};

export const isBlankString = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed === '' || trimmed === '(blank)' || trimmed === '.';
};

export const isUnknownString = (value: string): boolean => {
  return value === 'Unknown';
};

export const sortForDropdown = <T>(items: T[], anchor: (item: T) => string) => {
  return [...items].sort((a, b) => {
    const aAnchor = anchor(a);
    const bAnchor = anchor(b);

    const aIsBlank = isBlankString(aAnchor);
    const bIsBlank = isBlankString(bAnchor);

    if (aIsBlank && bIsBlank) {
      return 0;
    } else if (aIsBlank) {
      return 1;
    } else if (bIsBlank) {
      return -1;
    }

    const aIsNumber = !isNaN(Number(aAnchor));
    const bIsNumber = !isNaN(Number(bAnchor));

    if (aIsNumber && bIsNumber) {
      return Number(aAnchor) - Number(bAnchor);
    } else if (aIsNumber) {
      return -1;
    } else if (bIsNumber) {
      return 1;
    }

    return aAnchor.localeCompare(bAnchor);
  });
};

export const unknownToString = (value: unknown): string => {
  switch (typeof value) {
    case 'string':
      const trimmedValue = value.trim();

      if (isBlankString(trimmedValue)) {
        return '';
      }

      return value;
    case 'number':
      return value.toString();
    case 'bigint':
      return value.toString();
    case 'boolean':
      return value ? 'true' : 'false';
    case 'symbol':
    case 'undefined':
    case 'object':
    case 'function':
      return '';
    default:
      throw new Error(`Unexpected type: ${typeof value}`);
  }
};

export const bufferToString = (props: {
  buffer: Buffer;
  encoding?: BufferEncoding;
}): Result<string, BufferToStringError> => {
  const { buffer, encoding } = props;
  try {
    return okResult(buffer.toString(encoding ?? 'utf-8'));
  } catch (error) {
    return errResult({
      errorType: 'BufferToStringError',
      errorReason: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

export const isBasicObject = (
  value: unknown,
): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const BasicObject = z.record(z.string(), z.unknown());
export type BasicObject = z.infer<typeof BasicObject>;

export interface VagueResult {
  result: unknown;
  error: unknown;
}

export const Uuid = z.string().uuid();
export type Uuid = z.infer<typeof Uuid>;

export const sortWithReferenceOrderPlacingUnknownsLastAlphabetically = ({
  items,
  referenceOrder,
}: {
  items: string[];
  referenceOrder: string[];
}): string[] => {
  const referenceOrderMap: Record<string, number> = {};
  referenceOrder.forEach((item, index) => {
    referenceOrderMap[item] = index;
  });

  return [...items].sort((a, b) => {
    const aIndex = referenceOrderMap[a];
    const bIndex = referenceOrderMap[b];

    const aIsKnown = aIndex !== undefined;
    const bIsKnown = bIndex !== undefined;

    if (aIsKnown && bIsKnown) {
      return aIndex - bIndex;
    } else if (aIsKnown) {
      return -1;
    } else if (bIsKnown) {
      return 1;
    } else {
      return a.localeCompare(b);
    }
  });
};

export const raceTimeout = async <T, E extends BaseError>({
  promise,
  timeoutInSeconds,
}: {
  promise: Promise<Result<T, E>>;
  timeoutInSeconds: number;
}) => {
  let timeoutReached = false;

  const timeoutFn = async () => {
    await waitForMs(timeoutInSeconds * 1000);
    timeoutReached = true;
  };

  const raceResult = await Promise.race([promise, timeoutFn()]);

  if (timeoutReached || !raceResult) {
    return errResult({
      errorType: 'RaceTimeoutError',
      timeoutInSeconds,
    } satisfies RaceTimeoutError);
  }

  return raceResult;
};

export const produceMatrixOfItemIndexesInChunksOfSize = ({
  itemCount,
  chunkSize,
}: {
  itemCount: number;
  chunkSize: number;
}) => {
  return Array.from(
    {
      length: Math.ceil(itemCount / chunkSize),
    },
    (_, pageIndex) =>
      Array.from(
        { length: chunkSize },
        (_, i) => pageIndex * chunkSize + i,
      ).filter((index) => index < itemCount),
  );
};

export const produceArrayIndexesFromArray = <T>(array: T[]) => {
  return array.map((_, index) => index);
};

export class SimpleTTLCache<V> {
  private cache = new Map<string, { value: V; time: number }>();

  constructor(
    private ttl: number,
    private maxSize: number,
  ) {}

  get(key: string): V | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.time > this.ttl) {
      this.cache.delete(key);
      return undefined;
    }
    return entry.value;
  }

  set(key: string, value: V): void {
    const now = Date.now();
    this.cache.set(key, { value, time: now });

    // Evict old entries
    for (const [k, { time }] of this.cache) {
      if (now - time > this.ttl) this.cache.delete(k);
    }

    // Enforce max size
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) this.cache.delete(firstKey);
    }
  }
}
